import { useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { AuthLayout } from "@/components/AuthLayout";
import { authResetPassword, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { RefreshCw, CheckCircle2 } from "lucide-react";

const field = "w-full border-2 border-zinc-300 focus:border-[#2F6DA3] focus:outline-none px-4 py-3 text-sm transition-colors";
const label = "block text-[10px] font-mono uppercase tracking-[0.18em] text-zinc-500 mb-1.5";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    if (password !== confirm) { setError("Passwords don't match"); return; }
    setLoading(true); setError("");
    try {
      await authResetPassword(token, password);
      setDone(true);
      setTimeout(() => navigate("/login"), 2500);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <AuthLayout>
        <div className="text-center">
          <h1 className="font-display text-2xl font-extrabold tracking-tight mb-2">Invalid reset link</h1>
          <p className="text-sm text-zinc-500 mb-6">This link is missing its token. Please request a new one.</p>
          <Link to="/forgot-password"><Button className="bg-[#2F6DA3] text-white font-bold uppercase tracking-wider">Request new link</Button></Link>
        </div>
      </AuthLayout>
    );
  }

  if (done) {
    return (
      <AuthLayout>
        <div className="text-center" data-testid="reset-success">
          <div className="inline-flex bg-emerald-50 border-2 border-emerald-200 p-4 mb-5"><CheckCircle2 size={30} className="text-emerald-600" /></div>
          <h1 className="font-display text-3xl font-extrabold tracking-tight mb-2">Password updated</h1>
          <p className="text-sm text-zinc-500">Taking you to the login page…</p>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout>
      <h1 className="font-display text-3xl font-extrabold tracking-tight mb-1">Choose a new password</h1>
      <p className="text-sm text-zinc-500 mb-8">Make it at least 8 characters.</p>
      <form onSubmit={submit} className="space-y-4" data-testid="reset-form">
        <div>
          <label className={label}>New password</label>
          <input data-testid="reset-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={8} className={field} placeholder="••••••••" />
        </div>
        <div>
          <label className={label}>Confirm password</label>
          <input data-testid="reset-confirm" type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} required minLength={8} className={field} placeholder="••••••••" />
        </div>
        {error && <div data-testid="reset-error" className="text-sm text-red-700 bg-red-50 border border-red-200 px-3 py-2">{error}</div>}
        <Button type="submit" disabled={loading} data-testid="reset-submit" className="w-full bg-[#2F6DA3] hover:bg-[#234E74] text-white font-bold uppercase tracking-wider py-6">
          {loading ? <RefreshCw size={16} className="mr-2 animate-spin" /> : null} Update password
        </Button>
      </form>
    </AuthLayout>
  );
}
